import { useQuery } from '@tanstack/react-query';
import { auth } from '@cwos/api-client';
import type { NavGroup } from '@cwos/ui-admin';
import { narrowedTo, ORGANIZATION, UNKNOWN_POPULATION, type Population } from './figureState.js';

/**
 * Permission-shaped rendering (doc 06 §6.5, doc 12 §12.5, doc 13 T0).
 *
 * `/auth/me` carries `permissions[]` (doc 13 T0 — the unblocker). Setup/Settings
 * screens render ONLY when the principal holds the screen's permission; absent
 * items are ABSENT, never grayed-out (default-deny). While the backend one-field
 * addition is in flight the set is simply empty, so gated screens stay absent —
 * the UI never fabricates a permission to reveal a screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO QUESTIONS, AND THEY ARE NOT THE SAME QUESTION.
 *
 * Every grant this app reads has a `scopeType` and a `scopeId`. An org_admin can
 * mint a program-confined one today over ordinary HTTP — `POST /users/:id/roles`
 * and `POST /users/:id/permissions` both take the two fields, validate the scope
 * exists in-tenant, and store it (peopleSchemas.ts:11-23, people.ts:270-556,
 * identity.ts:412-431) — and `PersonDetail.tsx:272` already renders "· program
 * scoped" beside such a grant. No seed produces one and no admin control creates
 * one, so it is rare. Rare is not impossible, and it is not a test fixture.
 *
 *   1. MAY I REVEAL THIS SCREEN?  `has()`. Permissive on purpose. The API answers
 *      an out-of-scope read two different ways depending on the route — `programs`
 *      and the dashboard's dimensions NARROW to the grant and return 200,
 *      `directory/workers` and `supervision` REFUSE with 403 — and nothing in
 *      `/auth/me` says which kind a given key is. Gating on the organization-wide
 *      question would tell a program-scoped reader they have no access to a
 *      screen the server would serve them, narrowed: wrong in the direction that
 *      looks strict and is simply false.
 *
 *   2. DOES THIS READER REACH THE WHOLE ORGANIZATION?  `can()` / `reachOf()`.
 *      Anything that turns a permission into a SENTENCE ABOUT THE ORGANIZATION
 *      ("expired: 0", "That's everything — no hidden filters", "for your
 *      organization") or offers a verb over records the grant does not cover.
 *      Here a flattened `has()` is wrong in the dangerous direction: it promotes
 *      a program-scoped grant to organization-wide, which is the defect 61275c8
 *      fixed one screen at a time and three migrations cite by commit.
 *
 * `has()` used to be the only primitive, so question 2 was being answered with
 * question 1's tool everywhere it was asked. The two are now separate functions
 * with separate names, so that choosing between them is something a call site has
 * to do out loud.
 *
 * THE CARVE-OUT IN QUESTION 1, AND IT IS NARROW. Question 1 is permissive
 * because nothing in `/auth/me` says whether a key's route narrows or refuses.
 * That is a fact about keys IN GENERAL, not about every key. Where a specific
 * route has been READ and it refuses — 403, no shorter answer — the org-wide
 * question is the one the server is about to ask, and gating on it tells the
 * truth earlier instead of promising a screen that can only 403. The `may*`
 * predicates below are that carve-out: each names its route, its guard, and the
 * `can()` call inside it. Nothing else may take the exemption by analogy, and a
 * predicate that cannot cite a refusing guard stays on `has()` — a scope-strict
 * gate over a NARROWING route tells a program-scoped reader they have no access
 * to a screen the server would serve them.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Nav item extended with an optional gating permission (pure metadata). */
export interface GatedNavItem {
  key: string;
  label: string;
  icon: NavGroup['items'][number]['icon'];
  href: string;
  /** When set, the item renders only if the principal holds this permission. */
  permission?: string;
  /**
   * A HUB entry (owner hubs directive). The destinations inside the hub, in
   * landing-preference order — the hub's own face first, then its sections.
   *
   * Two rules, replacing `permission`/`href` for the entry itself, and both
   * exist to keep the two navigation laws true AT ONCE now that one rail item
   * fronts many screens:
   *
   *   VISIBLE when ANY destination inside is openable — hiding the whole hub
   *   from a principal who holds three of its screens is the
   *   capability-erasure defect (`location-discoverability.test.ts`), arriving
   *   through consolidation instead of around it.
   *
   *   POINTS AT the FIRST openable destination — a hub whose face the
   *   principal may not open (a supervisor without `audit:read` on
   *   Compliance) lands on the first section they hold instead of on a 403,
   *   which is the Time-clock defect this file's own history documents.
   *
   * "Openable" is {@link mayOpenScreen}, same as plain items.
   */
  enter?: ReadonlyArray<{ href: string; permission?: string }>;
  /**
   * The hub's real pages, for the sidebar accordion (owner navigation
   * directive §3) — full gated items so each child is filtered by the same
   * `mayOpenScreen` rule before it reaches the shell. Only meaningful
   * alongside `enter`.
   */
  childItems?: ReadonlyArray<GatedNavItem>;
}

export interface GatedNavGroup {
  key: string;
  label: string;
  /** Rendered pinned to the rail's bottom (the quiet admin foot). */
  foot?: boolean;
  items: GatedNavItem[];
}

/**
 * Pure permission check over the FLATTENED key list — an unheld or unknown key is
 * simply `false`.
 *
 * Question 1 above. This is the client's `holdsAtAnyScope`, and it carries the
 * same warning the server's version does: it ignores `scopeType`/`scopeId` by
 * construction, so it cannot answer "over the whole organization" and must not be
 * asked to. Reaching for it because `can()` refused somebody you expected it to
 * allow is exactly how the flattening defect comes back.
 */
export function hasPermission(permissions: readonly string[], key: string | undefined): boolean {
  if (!key) return true;
  return permissions.includes(key);
}

/**
 * THE ANALYTICS SURFACE READS WITH EITHER KEY, AND THE SERVER SAYS SO.
 *
 * `apps/api/src/domains/dashboard/accessPolicy.ts` declares
 * `AGGREGATE_READ = ['dashboard:read', 'analytics:read']` and applies it to
 * every aggregate route the `/dashboard` and `/dashboards` screens read —
 * overview, trends, clients, care-gaps, impact, filter-options, the metric
 * catalog, layouts, and the aggregate widgets. The two `DE_IDENTIFIABLE_ROUTES`
 * (`/analytics/staff`, `/analytics/schedule`) also take either key and shape the
 * payload per audience; `apps/admin/src/features/dashboard/analytics/disclosure.ts`
 * is the client half of that and already renders the de-identified variant.
 *
 * The client gated both routes on `dashboard:read` ALONE. The shipped `analyst`
 * role holds `analytics:read` and not `dashboard:read`
 * (1755046800000_seed-permissions.sql; 1755300000000_dashboard.sql records
 * "worker / analyst / service: NO dashboard:read"), so the one surface the role
 * exists to read was the one surface the client would not open — a screen built
 * for that audience, with its de-identification already written, behind a gate
 * that named the other audience's key.
 *
 * NOTHING HERE WIDENS THE SERVER. `analytics:read` still reaches only the
 * aggregate variants; `ROW_LEVEL_ROUTES` remain `dashboard:read` only and refuse
 * this reader at the door, which is why the fix can be a client gate and not a
 * permission change.
 */
/**
 * The keys that should open the Decision Queue.
 *
 * A SECOND KIND OF EQUIVALENT_READ_KEYS ROW, and the difference is worth
 * stating because the two rows mean different things.
 *
 * The `dashboard:read` row means "the server accepts either key on every route
 * behind this screen". This row does not: the Decision Queue is one page over
 * five sections that each gate themselves — timesheets on `timesheet:read`/`timesheet:approve`,
 * mileage on `mileage:review`, corrections on `time:read`/`timekeeping:manage`,
 * time off on `timeoff:read`/`timeoff:review`, clock exceptions on
 * `admin-views:read`. So this is a CLIENT door over self-gating content, and
 * putting it in that table would be claiming a server contract that does not
 * exist.
 *
 * What both rows have in common is the table's actual contract, which the
 * `dashboard:read` docblock states in capitals: NOTHING HERE WIDENS THE SERVER.
 * Each row exists so the client gate matches what the server already admits.
 * There it matches "either key, everywhere"; here it matches "this page is a
 * container, and its sections refuse on their own terms".
 *
 * The defect it fixes: the door was `timesheet:approve` alone, so a supervisor
 * holding `timeoff:review` could not open the page whose time-off section is
 * gated on exactly their key. It also keeps `mayOpenScreen` honest — that
 * function's own rule is that an entry must be present for everybody the route
 * admits, or the rail tells a reader they lack access the server is about to
 * grant them. Widening the route without this row would have broken exactly
 * that rule.
 */
export const DECISION_QUEUE_READ = [
  'timesheet:approve',
  'mileage:review',
  'timekeeping:manage',
  'timeoff:review',
  'admin-views:read',
] as const;

export const ANALYTICS_SURFACE_READ = ['dashboard:read', 'analytics:read'] as const;

/**
 * Keys the SERVER accepts in place of the key a screen names.
 *
 * A screen names ONE permission — the one its own doc comment is written
 * around — and that is the right thing for a screen to say. But a route family
 * whose guard is a LIST is not honestly described by one member of that list,
 * and the gap shows up as a rail entry that is absent for somebody the API
 * would serve. This table is where the difference is recorded, once, with the
 * server file that decides it named above.
 *
 * IT IS DELIBERATELY NOT A GENERAL ALIAS MECHANISM. An entry may be added only
 * when the server accepts the alternate key on EVERY route behind EVERY screen
 * gated by the named one — which is a fact to be read out of the API, not
 * assumed. `dashboard:read` qualifies today because the only two screens that
 * name it are the two analytics screens; `apps/admin/test/component/
 * analyst-analytics-reach.test.tsx` pins exactly which rail entries this opens,
 * so a future row-level screen gated on `dashboard:read` fails a test rather
 * than quietly appearing in an outside evaluator's rail.
 */
const EQUIVALENT_READ_KEYS: Record<string, readonly string[]> = {
  'dashboard:read': ANALYTICS_SURFACE_READ,
  'timesheet:approve': DECISION_QUEUE_READ,
};

/**
 * Every key that opens the screen named by `key` — the stated one, plus the
 * alternates the server declares for it.
 */
export function keysThatOpen(key: string): readonly string[] {
  return EQUIVALENT_READ_KEYS[key] ?? [key];
}

/**
 * Question 1, asked of the whole key list the ROUTE accepts rather than the one
 * key the screen names. Same flattened semantics as {@link hasPermission} and
 * the same warning: it cannot answer "over the whole organization".
 */
export function mayOpenScreen(permissions: readonly string[], key: string | undefined): boolean {
  if (!key) return true;
  return keysThatOpen(key).some((k) => permissions.includes(k));
}

/**
 * Drop items whose permission is not held, then drop groups left empty. Pure —
 * unit-tested. This is the single rule that turns the full nav map into what a
 * given principal may see (no disabled ghosts).
 *
 * "Not held" is {@link mayOpenScreen}, not {@link hasPermission}: an entry must
 * be present for everybody the route behind it admits, or the rail is telling a
 * reader they lack access the server is about to grant them.
 */
export function filterNavGroups(
  groups: readonly GatedNavGroup[],
  permissions: readonly string[],
): NavGroup[] {
  const out: NavGroup[] = [];
  for (const group of groups) {
    const items: NavGroup['items'] = [];
    for (const it of group.items) {
      if (it.enter) {
        // A hub entry: visible when any destination inside is openable, and it
        // points at the first openable one — see {@link GatedNavItem.enter}.
        // Its children (the sidebar accordion) pass the same rule one by one.
        const dest = it.enter.find((d) => mayOpenScreen(permissions, d.permission));
        if (dest) {
          const children = (it.childItems ?? [])
            .filter((c) => mayOpenScreen(permissions, c.permission))
            .map((c) => ({ key: c.key, label: c.label, href: c.href }));
          items.push({
            key: it.key,
            label: it.label,
            icon: it.icon,
            href: dest.href,
            ...(children.length > 0 ? { children } : {}),
          });
        }
      } else if (mayOpenScreen(permissions, it.permission)) {
        items.push({ key: it.key, label: it.label, icon: it.icon, href: it.href });
      }
    }
    if (items.length > 0) {
      out.push(
        group.foot
          ? { key: group.key, label: group.label, foot: true, items }
          : { key: group.key, label: group.label, items },
      );
    }
  }
  return out;
}

/**
 * WHAT A REFUSING-ROUTE PREDICATE READS, AND WHY IT IS NOT A FUNCTION.
 *
 * The predicates below are the exception the header's question 1 carves out for
 * itself. They gate routes that REFUSE an out-of-scope read rather than
 * narrowing it, and each one names the route and the guard it mirrors, so
 * "holds it over the organization" is the question the server is actually about
 * to be asked. They therefore read {@link PermissionsState.can}, not
 * {@link PermissionsState.has}.
 *
 * IT IS AN OBJECT ON PURPOSE. The obvious migration was to widen the parameter
 * from `(key) => boolean` to `(key, scope?) => boolean` — and TypeScript accepts
 * `has` for that: a function of fewer parameters is assignable to one of more,
 * and `(key: string | undefined) => boolean` is assignable to `(key: string,
 * scope?: …) => boolean` by ordinary contravariance. Every call site would have
 * kept compiling while still passing the FLATTENED key list, which is a no-op
 * that reads as a fix and would then be believed. `has` is not assignable to
 * `{ can }`, so a call site that has not moved is a compile error rather than a
 * silent promotion of a program-scoped grant to organization-wide.
 *
 * `usePermissions()` satisfies this structurally, so the ordinary call is
 * `mayListWorkers(usePermissions())` — or the state a screen already holds.
 *
 * DEFAULT-DENY, and that is right for what these gate: an `enabled:` flag and a
 * hidden control. It is NOT right for a sentence — `false` here covers "not
 * held", "held only over a program" and "not known yet" alike, and a screen that
 * renders "you may not see the roster" off a set that has not arrived is
 * accusing somebody of lacking access they may well hold. The screens below
 * claim the refusal only from a RESOLVED set (`!isLoading && !isError && !may…`),
 * the same shape `ShellLayout`, `Attention` and `RulesSettings` already use.
 */
export interface OrgWideReader {
  /**
   * The client's mirror of the server's `can()`. Defaults to the
   * ORGANIZATION-WIDE requirement, exactly as the guards these predicates
   * mirror do.
   */
  can: (key: string, scope?: auth.PermissionScopeQuery) => boolean;
}

/**
 * MAY THIS PRINCIPAL LIST THE STAFF OF THE ORGANIZATION?
 *
 * THE ROUTE REFUSES. `GET /directory/workers` (directory/routes.ts:520) is
 * `guard(request, reply, 'directory:read')`, and that guard is
 * `can(outcome.principal.authz, permission)` with no scope argument
 * (directory/routes.ts:123) — `can`'s default is `{ scopeType: 'org' }`
 * (auth/authorization.ts:129-131), so a program-scoped `directory:read` is a
 * 403, not a narrower list. The second key is charged the same way: the handler
 * calls `mayReadWorkerIdentity(principal.authz)`, which is `can(authz,
 * 'user:read')` at org scope (programs/routes.ts:127-129), and sends 403 naming
 * the key. The route says out loud why it refuses instead of filtering —
 * "Scoping this list to the supervisor edge … is DEAD WEIGHT for every
 * principal that exists" and "Every 200 this route returns is COMPLETE".
 *
 * So the organization-wide question is the right one here, and it is the one
 * this asks.
 *
 * `user:read` is the key that governs that fact everywhere on the server (`GET
 * /users`, the audit log's actor names, the alerts member list, the worker
 * identity fields inside the programs payloads).
 * 1755046800000_seed-permissions grants it to `org_admin` and `supervisor` and
 * deliberately not to `worker`.
 *
 * Six screens read that roster and each of them tested `has('directory:read')`
 * alone. This exists so there is ONE client-side statement of the rule rather
 * than six that can drift apart from the server and from each other — the same
 * reason `programs/routes.ts` keeps `mayReadWorkerIdentity` in one place on the
 * other side of the wire.
 *
 * It gates the `enabled:` of those queries, so an unheld key produces no request
 * and no cached 403 for the five screens that share the `['directory','workers']`
 * key; and it is the flag `nameResolver` takes, so a name the reader may not see
 * is reported as a PERMISSION fact rather than as a failed or pending read.
 */
export function mayListWorkers(reader: OrgWideReader): boolean {
  return reader.can('directory:read') && reader.can('user:read');
}

/**
 * MAY THIS PRINCIPAL READ THE ALERT INBOX?
 *
 * `GET /api/alerts` and `GET /api/alerts/:id` take `alerts:read` AND
 * `directory:read` (apps/api/src/domains/alerts/routes.ts:165,167 →
 * `requireDirectoryRead` at :151). The second key is there because alert TITLES
 * carry worker names — seven of the ten Tier-1 rules put one in — so reading
 * the inbox is reading the roster by another route.
 *
 * NOTHING on the client tested either key. The shell polls this read every 60
 * seconds on every screen, `Attention` polls it again, and `RightNow` reads it
 * a third time, all with no `enabled` guard at all — a two-key route asked with
 * zero keys checked.
 *
 * It is not hypothetical. 1757290000000_analyst-alerts-read-grants-nothing
 * REMOVED `alerts:read` from the `analyst` role precisely because
 * `directory:read` had made it inert, and that migration spells out why the
 * alternative — granting an analyst `directory:read` — is refused on the
 * merits: the role exists for "a board member, a funder's evaluator, an outside
 * contractor — who is trusted with COUNTS and not with people". So an analyst
 * signs in, holds neither key, and every screen quietly 403s once a minute
 * while the bell shows a confident 0 and the panel offers to try again.
 *
 * A zero on that badge is the same class of lie as `expired: 0` on a scoped
 * credentials count: it is a claim about the organization made out of a refusal.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONE OF THE FOUR STILL ON `has`, AND IT IS NOT BECAUSE ITS ROUTE NARROWS.
 *
 * Its route refuses, exactly like the other three, and the evidence is the same
 * shape: `GET /api/alerts` (alerts/routes.ts:284) and `GET /api/alerts/:id`
 * (:299) are `guard(request, reply, 'alerts:read')`, whose body is
 * `can(outcome.principal.authz, permission)` with no scope (:213) — org-wide by
 * `can`'s default — and then `requireDirectoryRead` (:267), which is
 * `can(principal.authz, 'directory:read')` and sends 403 naming the key. Its
 * own comment states the rule this file states: "`can()`, not
 * `holdsAtAnyScope`: naming every worker in the organization is an org-wide
 * disclosure". The LIST then narrows rows by caseload
 * (`readsWholeOrganization`, :218), but that narrowing happens BEHIND a gate
 * that already refused; it is not an alternative to it.
 *
 * What stops it is not the question, it is the atom. This predicate has three
 * call sites — `ShellLayout`, `Attention` and `RightNow` — and `RightNow` is
 * held by another lane. Migrating the signature is what makes an unmoved call
 * site a compile error, which is the whole value of the change and also why it
 * cannot land two-thirds of the way: widening it to `(key, scope?)` instead so
 * `RightNow` keeps compiling is precisely the silent no-op {@link OrgWideReader}
 * exists to prevent. So it stays flattened, whole, and wrong in the direction
 * that reveals rather than hides — the permissive gate the header describes.
 *
 * TO FINISH IT (one commit, three files): change this parameter to
 * `OrgWideReader` and its body to `reader.can(...)`; then `ShellLayout.tsx:52`,
 * `Attention.tsx:61` and `RightNow.tsx:84` each pass the `usePermissions()`
 * state instead of its `has`. All three already compute their refusal from a
 * resolved set, so nothing else on those screens moves.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function mayReadAlerts(has: (key: string | undefined) => boolean): boolean {
  return has('alerts:read') && has('directory:read');
}

/**
 * MAY THIS PRINCIPAL READ CONNECTOR HEALTH?
 *
 * `GET /integrations/connectors` charges `integrations:read` alone — no
 * second key the way alerts charges `directory:read` alongside `alerts:read`,
 * because a connector names no worker (packages/api-client/src/
 * integrations.ts's own docblock: "connection health, which a supervisor
 * holds").
 *
 * This gates the ONE optional cross-check `dataHealth.ts`'s `useDataHealthCheck`
 * makes: whether Needs Attention's, Right Now's and Insights' "nothing is
 * being missed" claims may say so unconditionally, or must name a known
 * data-health gap instead. A reader without this key gets `{kind: 'unknown'}`
 * from that hook (PH-ATT-001) — never asked is an unanswered question, not a
 * healthy one, so the caveated sentence renders rather than the plain claim —
 * and the request is never fired at all, so a reader who cannot hold the key
 * does not spend a round trip on a 403, and no unrelated screen is refused
 * over one optional read.
 */
export function mayReadIntegrationsHealth(has: (key: string | undefined) => boolean): boolean {
  return has('integrations:read');
}

/**
 * MAY THIS PRINCIPAL READ THE ALERTS ONE RULE PRODUCED?
 *
 * THE ROUTE REFUSES. `GET /rules/:id/alerts` (rules/routes.ts:288) is
 * `guard(request, reply, 'rules:read')` — `can(outcome.principal.authz,
 * permission)` with no scope, org-wide by default (:96) — and then
 * `requireDirectoryRead` (:291 → :135), which is `can(principal.authz,
 * 'directory:read')` and returns a 403 naming the key. Both halves are 403s;
 * neither returns a shorter list. The route says so itself: "REFUSES RATHER
 * THAN REDACTS … the name is baked into stored free text, there is no field to
 * null", and "`can()` at organization scope, never a membership test on a
 * flattened key list". The reason is the same as the inbox's: "a rule's alerts
 * name the worker they are about", in the server's own 403 message.
 *
 * The panel fired it on neither key, and the refusal landed in
 * `HistoryUnavailable` — "We couldn't read the exceptions this rule raised … try
 * again". Every try returns the same 403.
 *
 * The route's own comment is honest that no SHIPPED role is caught by this —
 * org_admin and supervisor hold both keys — and that "what this closes is the
 * grantable one: a tenant-custom role, or a direct POST /users/:id/permissions
 * grant, carrying `rules:read` without it". Which is precisely the principal
 * this panel would lie to, and the one an administrator creates without being
 * told the combination is incomplete.
 */
export function mayReadRuleAlerts(reader: OrgWideReader): boolean {
  return reader.can('rules:read') && reader.can('directory:read');
}

/**
 * THE KEY THAT ACTUALLY GOVERNS THE TIME-CLOCK SCREENS.
 *
 * Every `/api/admin-views/*` read — the workforce board, one session's detail,
 * the verification summary, the exception panel — is guarded by
 * `admin-views:read` (apps/api/src/domains/admin-views/routes.ts:133,142,178,204).
 * `time:read` guards the `/timekeeping/*` routes, and NONE of these screens
 * calls one.
 *
 * The two are not interchangeable, and the seed says so in as many words.
 * 1755220000000_admin-views-permission grants `admin-views:read` to org_admin
 * and supervisor and records the omission for the rest: "worker: NO
 * admin-views:read. Workers see their own sessions through the time:read
 * routes, not the admin dashboard." 1755064800000_time-clock grants `time:read`
 * to org_admin, supervisor AND worker.
 *
 * So a `worker` principal held the key the rail and the route asked for, was
 * admitted to four screens, and every panel on them 403'd. Holding `time:read`
 * has not implied access to what these screens render since the day
 * `admin-views:read` was minted.
 */
export const ADMIN_VIEWS_READ = 'admin-views:read';

/**
 * MAY THIS PRINCIPAL READ ONE SESSION'S ROUTE HISTORY?
 *
 * `GET /location/sessions/:id/timeline` applies TWO gates
 * (apps/api/src/domains/location/routes.ts — `guard(..., 'location_history:view',
 * ORG_WIDE_LOCATION_HISTORY)` and then `requireTrailDoor`, which opens with
 * `if (!can(principal.authz, 'breadcrumb:view'))` → 403):
 *
 *   location_history:view — the org-wide Track A review surface. It is what the
 *     workforce MAP needs, and the map is all it needs.
 *   breadcrumb:view       — the raw breadcrumb polyline. The timeline hands back
 *     `repo.listBreadcrumbsForSession`, the identical array
 *     `GET /location/sessions/:id/breadcrumbs` serves, so it now pays the
 *     identical price. Before that server fix the narrow door refused what the
 *     wide door handed over, with punches, stops and gaps on top.
 *
 * BOTH are granted to NO role by default; each is a deliberate direct grant. So
 * "holds one and not the other" is not an exotic case here — it is the ordinary
 * result of granting the map and not the trail, and that principal is exactly
 * who was being shown a "View route history" button that 403s.
 *
 * The server REFUSES; it does not hand back a timeline with the breadcrumbs
 * removed. So a read fired without the second key comes back 403, and a screen
 * gated on the first key alone renders a permanent refusal as a retry.
 *
 * AND IT REFUSES AT ORG SCOPE, WHICH THIS ROUTE STATES OUTRIGHT. The first gate
 * does not merely inherit `can()`'s default — it names the scope at the call
 * site, `guard(request, reply, 'location_history:view',
 * ORG_WIDE_LOCATION_HISTORY)` where `ORG_WIDE_LOCATION_HISTORY` is
 * `{ scopeType: 'org' }` (location/routes.ts:703, 730-735), with the reasoning
 * beside it: "a PROGRAM-scoped `location_history:view` row does NOT open these
 * routes; it is refused outright rather than silently promoted to org-wide,
 * which is the 61275c8 defect stated from the safe side. Enforcing
 * program-scope as a NARROWER read … is the later hardening; refusing it is the
 * correct behaviour until then." The second gate is `requireTrailDoor` (:372),
 * `can(principal.authz, 'breadcrumb:view')` at the default org scope → 403.
 *
 * This is the one of the four where a scope-strict gate is not merely allowed
 * but required to match: a program-scoped holder gated on the flattened list
 * was offered "View route history" for a route that had already decided to
 * refuse them by scope.
 */
export function mayReadSessionTrail(reader: OrgWideReader): boolean {
  return reader.can('location_history:view') && reader.can('breadcrumb:view');
}

/**
 * The key a trail-less reader is actually missing, for the denied state.
 *
 * Naming `location_history:view` there would be wrong twice over: they hold it
 * (it is how they reached the screen at all), and being sent to ask an admin
 * for a permission you already have is a dead end.
 */
export const SESSION_TRAIL_KEY = 'breadcrumb:view';

/**
 * MAY THIS PRINCIPAL CHANGE A WORKER RECORD?
 *
 * THE ROUTE REFUSES, and at org scope. `PATCH /directory/workers/:id` is
 * `guard(request, reply, 'directory:manage')` (directory/routes.ts:631), and
 * `guard` is `can(outcome.principal.authz, permission)` with no scope argument
 * (:123) — `can`'s default is `{ scopeType: 'org' }`. A `directory:manage`
 * confined to one program is a 403 on every worker in the organization,
 * including the ones inside that program, so the org-wide question is the one
 * the server is about to ask.
 *
 * It is a GATE ON AN EDIT CONTROL, not on a sentence, so default-deny is right:
 * while the permission set is unknown the button is simply not there.
 *
 * WHAT IT DOES NOT PROMISE. The route asks a SECOND question this client cannot
 * mirror — `reachesWorkerRecord`, which is `user:read` OR the caller's own
 * `worker.supervisor_id` edge (:239). That is a fact about the pair of them and
 * lives in the database; the screen learns it the same way the reader does,
 * from the 403 the save comes back with.
 */
export function mayEditWorkerRecord(reader: OrgWideReader): boolean {
  return reader.can('directory:manage');
}

/**
 * MAY THIS PRINCIPAL WRITE THE TWO FIELDS THAT DECIDE WHO SEES WHOM?
 *
 * `assertMayAssignAuthority` (directory/service.ts:238) refuses a patch
 * containing `supervisorId` or `userId` unless `can(ctx.authz,
 * 'directory:assign')` — org-wide, and the function says at the call site why a
 * flattened membership test would not do: "a `directory:assign` grant scoped to
 * ONE PROGRAM was read as organization-wide — so an administrator devolving
 * assignment inside a single program would have handed out the whole escalation
 * this guard exists to prevent."
 *
 * SEPARATE FROM {@link mayEditWorkerRecord} BECAUSE THE SERVER SEPARATES THEM,
 * and the reason is worth restating where the control is drawn.
 * `worker.supervisor_id` is the edge six domains resolve reach through, so a
 * caller who can write it can appoint themselves anybody's supervisor and read
 * everything that unlocks. `worker.user_id` is worse: `worker_org_user_uk`
 * makes the binding unique and `resolveOwnWorkerId` is exactly that lookup, so
 * moving a `user_id` does not grant reach over another worker — it makes the
 * caller BE them. The seeded `supervisor` role holds `directory:manage`
 * org-wide and does not hold this.
 */
export function mayAssignWorkerAuthority(reader: OrgWideReader): boolean {
  return reader.can('directory:assign');
}

export interface PermissionsState {
  /**
   * The distinct keys held, at ANY scope. Question 1's data.
   *
   * `/auth/me` builds it as `permissionScopes.map(g => g.key)`, so the two fields
   * that decide a grant are already gone by the time it reaches here. Nothing
   * read off this array can distinguish a program-scoped grant from an
   * organization-wide one — see {@link can} for anything that needs to.
   */
  permissions: string[];
  /**
   * Question 1 — does the principal hold this key SOMEWHERE? Reveals a screen,
   * gates an `enabled:`, chooses between "you don't hold this" and everything
   * else. Deliberately permissive; see the header for why a gate must be.
   */
  has: (key: string | undefined) => boolean;
  /**
   * Question 2 — the client's mirror of the server's `can()`, scope and all.
   *
   * Defaults to the ORGANIZATION-WIDE requirement, exactly as
   * `apps/api/src/domains/auth/authorization.ts` does, so `can('credential:manage')`
   * asks the question a sentence about the organization actually depends on. Pass
   * a scope for the narrower question (`can('program:read', { scopeType:
   * 'program', scopeId })`).
   *
   * DEFAULT-DENY, which means it is `false` while the set is loading and `false`
   * when it failed — right for hiding a control, WRONG for labelling a figure,
   * because it makes an org_admin's screen say "your team" on every cold load.
   * That is the second half of this defect and it has its own answer:
   * {@link reachOf}, which reports `unknown` instead of guessing.
   */
  can: (key: string, scope?: auth.PermissionScopeQuery) => boolean;
  /**
   * The grants as the server stated them, or `undefined` when it did not state
   * them — an API deployed before `/auth/me` carried `permissionScopes`, or no
   * answer yet. `undefined` is not "no grants": `can()` and `has()` both handle
   * the distinction, and no screen should be reading this array directly.
   */
  grants: readonly auth.PermissionGrant[] | undefined;
  /**
   * WHO THE READER IS — the `userId` from `/auth/me`, or `null` while the set
   * is unresolved or failed.
   *
   * Emphatically NOT a permission and never a substitute for one. It answers
   * exactly one question that no permission can: is this row MINE. Withdrawing
   * a time correction is the case it was added for — only the person who filed
   * one may take it back, and `requested_by_user_id` on the row is what says
   * so — and the server re-checks that under the row lock regardless of what
   * this client concluded.
   *
   * `null` is UNRESOLVED, not "somebody else". A comparison against it must
   * therefore fail closed: `null === row.requestedByUserId` is `false` for
   * every real row, which is the direction that hides a control rather than
   * offering one that 403s. It is also why this is nullable instead of an empty
   * string, which would compare equal to a null column.
   */
  userId: string | null;
  isLoading: boolean;
  /**
   * The permission set could not be read. `permissions` is empty in this case
   * for the same default-deny reason as always — but empty-because-unknown is
   * NOT the same as empty-because-unheld, and a caller that renders "you lack
   * this permission" off the first one is telling the user something false
   * about their own account. `<RequirePermission>` reads this.
   */
  isError: boolean;
  /** Ask for the set again — the retry behind the guard's error state. */
  refetch: () => void;
}

/**
 * Reads the principal's permission set off `auth.me()` (cached). Powers rail
 * visibility and `<RequirePermission>` guards. Never throws to the screen — a
 * failed/absent set resolves to empty (default-deny), and `isError` says which
 * kind of empty it is.
 */
export function usePermissions(): PermissionsState {
  const query = useQuery({
    queryKey: ['auth', 'me', 'permissions'],
    queryFn: () => auth.me(),
    staleTime: 5 * 60_000,
  });
  const permissions = query.data?.permissions ?? [];
  return {
    permissions,
    has: (key) => hasPermission(permissions, key),
    // The scope-aware answer, delegated to `@cwos/api-client` rather than
    // reimplemented here. It is a MIRROR of the server's `can()` and there must
    // be exactly one of it on this side of the wire: a second copy in the admin
    // app is a second thing to drift, and drift is the whole failure mode of a
    // client-side reconstruction of a server rule.
    can: (key, scope) => auth.can(query.data, key, scope),
    grants: query.data?.permissionScopes,
    userId: query.data?.userId ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

/**
 * HOW FAR THIS READER'S OWN GRANTS REACH, in the vocabulary `figureState`
 * already has for it.
 *
 * `can()` is default-deny and therefore collapses three different facts into
 * `false`: not held, held only over a program, and NOT YET KNOWN. That collapse
 * is correct for hiding a control and is the second half of this defect
 * everywhere else — it is why an org-wide admin's screen rendered as narrowed on
 * every cold load, before the permission set had arrived, with nothing on the
 * page admitting that it was a guess.
 *
 * `Population` is the distinction, already spelled here and nowhere else:
 * `organization` / `narrowed` / `unknown` (figureState.ts). This is the bridge
 * from a permission set to it, and there is deliberately no fourth spelling.
 *
 * WHY "NOT HELD" AND "HELD OVER ONE PROGRAM" ARE THE SAME ANSWER. Both mean the
 * reader does not reach the organization, which is the only thing a label is
 * allowed to say. What the reader DOES reach is a different question, and it is
 * one this app cannot answer from `/auth/me` — a grant carries a `scopeId`, not
 * the rows behind it — so `who` is supplied by the caller, who knows which
 * server rule they are describing. That is the same shape `narrowedTo` already
 * demands and for the same reason: choosing the phrase is the moment somebody
 * has to notice there was a choice.
 *
 * `keys` is a list because the server's own predicates are: `hasOrgWideReach`
 * (credentials/service.ts:146) widens on `credential:manage` OR
 * `credential:verify`. ANY key held organization-wide is enough, matching `||`.
 */
export function reachOf(
  state: Pick<PermissionsState, 'can' | 'isLoading' | 'isError'>,
  keys: readonly string[],
  who: string,
): Population {
  // Empty-because-unknown is not empty-because-unheld. `usePermissions` resolves
  // a failed read to an empty set for default-deny reasons, which is right for
  // hiding a screen and wrong for labelling a number: it would make every figure
  // read "counts your team" for a person who may in fact hold the organization.
  if (state.isLoading || state.isError) return UNKNOWN_POPULATION;
  for (const key of keys) {
    // No scope argument — the ORGANIZATION-WIDE question, which is the only one
    // that licenses the word "organization" under the figure.
    if (state.can(key)) return ORGANIZATION;
  }
  return narrowedTo(who);
}
